import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import FlexBox from 'components/common/FlexBox';
import { get, sequence, set } from '@totalsoft/change-tracking-react';
import LeasingIncomeSchemaEventComponent from './LeasingIncomeSchemaEventComponent';
import {
  ENTITY_EVENTS,
  LEASING_EXPENSE_MEASURING_UNIT,
  LEASING_EXPENSE_NEGOTIATION_MOMENTS,
  LEASING_EXPENSE_TYPES
} from 'features/leasingIncomeSchema/queries';
import useQuery from 'hooks/apollo/useQuery';
import { CURRENCIES_DICTIONARY_QUERY } from 'features/common/queries';
import { Card, Typography } from '@totalsoft/rocket-ui';
import IconButton from 'components/common/buttons/IconButton';
import { useTranslation } from 'react-i18next';

const getEmptyDetail = schemaId => ({
  id: crypto.randomUUID(),
  schemaId,
  eventId: null,
  incomeTypeId: null,
  currencyId: null,
  currencyValue: null,
  currencyPercent: null,
  minValue: null,
  maxValue: null,
  measuringUnitId: null,
  negotiationMomentId: null,
  precondition: null,
  formula: null,
  negotiationLimits: []
});

const LeasingIncomeSchemaEventsComponent = ({ schemaId, lens, onSave, validation, disabled, isDirty }) => {
  const { t } = useTranslation();

  // #region Collections

  const { data: entityEventsData } = useQuery(ENTITY_EVENTS);

  const { data: incomeTypesData } = useQuery(LEASING_EXPENSE_TYPES);

  const { data: currenciesData } = useQuery(CURRENCIES_DICTIONARY_QUERY);

  const { data: leasingExpenseMeasuringUnitsData } = useQuery(LEASING_EXPENSE_MEASURING_UNIT);

  const { data: leasingExpenseNegotiationMomentsData } = useQuery(LEASING_EXPENSE_NEGOTIATION_MOMENTS);

  const entityEvents = useMemo(() => entityEventsData?.entityEvents || [], [entityEventsData]);

  const incomeTypes = useMemo(() => incomeTypesData?.leasingExpenseTypesCollection || [], [incomeTypesData]);

  const currencies = useMemo(() => currenciesData?.currenciesDictionary || [], [currenciesData]);

  const leasingExpenseMeasuringUnits = useMemo(
    () => leasingExpenseMeasuringUnitsData?.leasingExpenseMeasuringUnits || [],
    [leasingExpenseMeasuringUnitsData]
  );

  const leasingExpenseNegotiationMoments = useMemo(
    () => leasingExpenseNegotiationMomentsData?.leasingExpenseNegotiationMoments || [],
    [leasingExpenseNegotiationMomentsData]
  );

  // #endregion

  const handleAdd = () => {
    const current = lens |> get;
    (lens |> set)([...current, getEmptyDetail(schemaId)]);
  };

  const handleDelete = id => {
    const current = lens |> get;
    (lens |> set)(current.filter(detail => detail.id !== id));
  };

  const hasDetails = (lens.length |> get) > 0;

  return (
    <Card disablePadding>
      <FlexBox direction='column' gap={1.5} px={2} py={2}>
        <FlexBox justifyContent='end' gap={0.5} px={1}>
          <IconButton type='add' onClick={handleAdd} disabled={disabled} />
          <IconButton type='save' onClick={onSave} disabled={!isDirty || disabled} />
        </FlexBox>
        {!hasDetails && (
          <Typography color='textPrimary' align='center'>
            {t('LeasingIncomeSchema.NoEvent')}
          </Typography>
        )}
        {hasDetails &&
          (lens |> sequence).map((detailLens, index) => (
            <LeasingIncomeSchemaEventComponent
              key={detailLens.id |> get}
              lens={detailLens}
              entityEvents={entityEvents}
              incomeTypes={incomeTypes}
              currencies={currencies}
              leasingExpenseMeasuringUnits={leasingExpenseMeasuringUnits}
              leasingExpenseNegotiationMoments={leasingExpenseNegotiationMoments}
              onDelete={handleDelete}
              validation={validation[index]}
              disabled={disabled}
            />
          ))}
      </FlexBox>
    </Card>
  );
};

LeasingIncomeSchemaEventsComponent.propTypes = {
  schemaId: PropTypes.number,
  lens: PropTypes.object,
  onSave: PropTypes.func,
  validation: PropTypes.object,
  disabled: PropTypes.bool,
  isDirty: PropTypes.bool
};

export default LeasingIncomeSchemaEventsComponent;
